import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function Login() {
  const { user, loadSession, t, lang, setLang } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (user) return <Navigate to="/" replace />;

  const done = async (data) => {
    toast.dismiss();
    if (data.access_token) sessionStorage.setItem("tk", data.access_token);
    await loadSession();
    nav(data.must_enroll_mfa && !data.mfa_enabled ? "/account" : "/");
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = mfa
        ? await api.post("/auth/mfa/verify", { mfa_token: mfa, code })
        : await api.post("/auth/login", { email, password });
      if (data.mfa_required) setMfa(data.mfa_token);
      else await done(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden flex-col justify-between bg-[#283F91] p-12 text-white lg:flex">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/70">PrEP Patient Monitoring</div>
        <div>
          <h1 className="max-w-md text-5xl font-bold leading-tight">Caseloads, prescriptions and care retention in one place.</h1>
          <p className="mt-6 max-w-md text-white/75">Secure internal system for Latinos Salud staff. Access is limited to accounts created by an administrator.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70"><ShieldCheck size={14} /> Role-based access · Audit logging · Session timeout</div>
      </div>
      <div className="flex items-center justify-center bg-[var(--surface)] p-6">
        <form onSubmit={submit} className="panel w-full max-w-sm p-8" data-testid="login-form">
          <div className="mb-2 flex justify-end gap-1 text-xs">
            {["en", "es"].map((l) => (
              <button type="button" key={l} onClick={() => setLang(l)} className={`rounded px-2 py-1 font-semibold ${lang === l ? "bg-[#283F91] text-white" : "text-[var(--muted-ink)]"}`} data-testid={`login-lang-${l}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <img src="/brand/logo.png" alt="Latinos Salud" className="mx-auto mb-6 h-24 w-auto" />
          {!mfa ? (
            <>
              <label className="lbl">{t("Email")}</label>
              <input className="inp mb-4 mt-1.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              <label className="lbl">{t("Password")}</label>
              <input className="inp mb-6 mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </>
          ) : (
            <>
              <label className="lbl">Authenticator code</label>
              <input className="inp mb-6 mt-1.5 tracking-[0.4em]" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required data-testid="login-mfa-input" />
            </>
          )}
          <button className="btn btn-primary w-full justify-center" disabled={busy} data-testid="login-submit-button">{busy ? "…" : t("Sign in")}</button>
          <p className="mt-6 text-center text-[11px] text-[var(--muted-ink)]">No public registration. Contact your administrator for access.</p>
        </form>
      </div>
    </div>
  );
}
